// WIDGET — Controls with Highest Failure Rate: treemap ranked by failure rate, drill-down to the
// profiles/plans each control failed in.
// Self-fetching: calls its own dedicated endpoint
// (GET /dashboard/grc/system-owner/control-failure-rates) rather than a shared rollup.
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { Tooltip, ResponsiveContainer, Treemap } from "recharts";
import { Card, SectionTitle } from "../../GRCManagerDashboard/grcDashboardWidgets";
import { DataTable, type Column } from "../../../components/DataTable";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { EmptyState } from "../../../components/EmptyState";
import { SkeletonList } from "../../../components/Skeleton";
import { SEVERITY_ORDER, severityHex, severityLabel, type ControlSeverity } from "../data/applicationKriData";
import {
  fetchControlFailureRates,
  type SystemOwnerControlFailureRow,
  type SystemOwnerControlFailureRatesResponse,
} from "../../../../api/frameworksApi";

/* Every KRI widget card is this tall — matched to the Organization view's
   cards (a 320px chart body plus header and card padding) so both tabs read
   as the same size. */
const KRI_CARD = "flex h-[520px] flex-col";

function WidgetHeader({ title, subtitle, onViewMore }: { title: string; subtitle?: string; onViewMore?: () => void }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      <SectionTitle title={title} subtitle={subtitle} />
      {onViewMore && (
        <button
          onClick={onViewMore}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-500/50 px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
        >
          View More <ArrowRight className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: ControlSeverity | null }) {
  const hex = severityHex(severity);
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold"
      style={{ background: `${hex}1f`, color: hex }}
    >
      {severityLabel(severity)}
    </span>
  );
}

/* ══ Controls with Highest Failure Rate — drill-down drawer ══════════════ */
function ControlFailureRateDrawer({
  open,
  onOpenChange,
  rows,
  selectedId,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  rows: SystemOwnerControlFailureRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const detail = selectedId ? (rows.find((r) => r.controlId === selectedId) ?? null) : null;

  const listColumns: Column<SystemOwnerControlFailureRow>[] = [
    {
      key: "controlId",
      header: "Control",
      align: "left",
      headerAlign: "left",
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{r.controlId}</p>
          {r.controlTitle && <p className="text-xs text-muted-foreground">{r.controlTitle}</p>}
        </div>
      ),
    },
    { key: "severity", header: "Severity", render: (r) => <SeverityBadge severity={r.severity} /> },
    { key: "failureRate", header: "Failure Rate", render: (r) => <span className="text-sm font-bold tabular-nums text-foreground">{r.failureRate}%</span> },
    { key: "failures", header: "Failures", render: (r) => <span className="text-sm tabular-nums text-foreground/80">{r.failures}</span> },
  ];

  const failedInColumns: Column<{ key: string; profileTitle: string; planTitle: string }>[] = [
    {
      key: "planTitle",
      header: "Plan",
      align: "left",
      headerAlign: "left",
      render: (r) => <span className="text-sm text-foreground">{r.planTitle}</span>,
    },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onSelect(null); }}>
      <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 overflow-hidden border-l border-border bg-background p-0 sm:max-w-[1160px]">
        {detail ? (
          <div key={`detail-${selectedId}`} className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6" style={{ borderLeftWidth: 3, borderLeftColor: severityHex(detail.severity) }}>
              <button onClick={() => onSelect(null)} className="mb-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                &larr; Back to all controls
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground">{selectedId}</h2>
                <SeverityBadge severity={detail.severity} />
              </div>
              {detail.controlTitle && <p className="mt-1 text-sm text-muted-foreground">{detail.controlTitle}</p>}
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <span>Failure rate: <b className="text-foreground">{detail.failureRate}%</b></span>
                <span>Failures: <b className="text-foreground">{detail.failures}</b></span>
                <span>Evaluations: <b className="text-foreground">{detail.evaluations}</b></span>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <p className="mb-2 text-xs font-semibold text-foreground">Failed in</p>
              {detail.failedInProfiles.length ? (
                <DataTable
                  columns={failedInColumns}
                  data={detail.failedInProfiles.flatMap((p, i) =>
                    p.plans.map((pl) => ({
                      key: `${p.profileId ?? `profile-${i}`}-${pl.planId}`,
                      profileTitle: p.profileTitle ?? "(no profile)",
                      planTitle: pl.planTitle ?? pl.planId.slice(-8),
                    })),
                  )}
                  rowKey={(r) => r.key}
                  emptyText="This control has not failed in any plan."
                  hidePageButtons
                  compact
                />
              ) : (
                <p className="text-sm text-muted-foreground">This control has not failed in any plan.</p>
              )}
            </div>
          </div>
        ) : (
          <div key="list" className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-left-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6">
              <h2 className="text-xl font-bold text-foreground">Controls with Highest Failure Rate</h2>
              <p className="mt-1 text-sm text-muted-foreground">All {rows.length} controls — select one to see the profiles and plans it failed in</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <DataTable columns={listColumns} data={rows} rowKey={(r) => r.controlId} emptyText="No Data Available" onRowClick={(r) => onSelect(r.controlId)} hidePageButtons compact />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ControlFailureTreemapTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { controlId: string; controlTitle: string | null; severity: ControlSeverity | null; failureRate: number; failures: number; evaluations: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.controlId}{p.controlTitle ? ` — ${p.controlTitle}` : ""}</p>
      <p style={{ color: severityHex(p.severity) }}>{severityLabel(p.severity)}</p>
      <p className="text-muted-foreground">
        Failure rate: <span className="font-semibold text-foreground">{p.failureRate}%</span> ({p.failures}/{p.evaluations})
      </p>
      <p className="text-muted-foreground">Assessed {p.evaluations} time{p.evaluations === 1 ? "" : "s"}</p>
    </div>
  );
}

/* Single treemap tile — tile size = failure rate, color = the control's severity (the API's
   worst-case POA&M severity for that control). Rendered via foreignObject (real HTML inside the
   SVG) so it can use the app's normal Tailwind dark: classes, instead of a flat SVG rect that
   would need its own separate dark-theme color logic. Clicking a tile drills straight into that
   control's detail, same as clicking a row anywhere else on this page. */
function ControlFailureTreemapTile(props: any) {
  const { x, y, width, height, controlId, severity, failureRate, onSelect } = props;
  if (!width || !height || failureRate === undefined) return null;
  const hex = severityHex(severity ?? null);
  const showId = width > 34 && height > 26;
  const showDetails = width > 46 && height > 48;
  const geometryTransition = "x 450ms ease, y 450ms ease, width 450ms ease, height 450ms ease";
  return (
    <g style={{ transition: geometryTransition }}>
      <foreignObject x={x + 1.5} y={y + 1.5} width={Math.max(0, width - 3)} height={Math.max(0, height - 3)}>
        <div
          onClick={() => controlId && onSelect?.(controlId)}
          className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-0.5 overflow-hidden rounded-lg border px-1.5 py-1 text-center transition-shadow hover:shadow-md"
          style={{ background: `${hex}40`, borderColor: `${hex}99` }}
        >
          {showId && <p className="w-full truncate text-xs font-bold leading-tight text-foreground">{controlId}</p>}
          {showDetails && <p className="w-full truncate text-sm font-bold leading-tight tabular-nums" style={{ color: hex }}>{failureRate}%</p>}
        </div>
      </foreignObject>
    </g>
  );
}

/* ══ Controls with Highest Failure Rate — main export ═══════════════════ */
export function ControlFailureRateWidget() {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [data, setData] = useState<SystemOwnerControlFailureRatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchControlFailureRates()
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load control failure rates"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Server-sorted (severity desc -> failureRate desc -> failures desc -> controlId asc), so this
  // renders as-is. Treemap surfaces only the top 5; "View More" opens the drawer's full list.
  const rows = data?.controls ?? [];
  const treemapData = rows.slice(0, 5).map((r) => ({
    controlId: r.controlId,
    controlTitle: r.controlTitle,
    severity: r.severity,
    failureRate: r.failureRate,
    failures: r.failures,
    evaluations: r.evaluations,
    size: Math.max(r.failureRate, 2),
  }));
  const openControl = (id: string) => { setSel(id); setOpen(true); };

  const showSkeleton = loading && !data;
  const showError = !!error && !data;

  const subtitle =
    "Identifies controls with the highest failure frequency to prioritize remediation and risk reduction.";

  return (
    <Card className={KRI_CARD}>
      <WidgetHeader
        title="Controls with Highest Failure Rate"
        subtitle={subtitle}
        onViewMore={rows.length ? () => { setSel(null); setOpen(true); } : undefined}
      />
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {SEVERITY_ORDER.map((s) => (
          <span key={severityLabel(s)} className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: severityHex(s) }} />
            {severityLabel(s)}
          </span>
        ))}
      </div>
      <div className="min-h-0 w-full flex-1">
        {showSkeleton ? (
          <SkeletonList rows={5} />
        ) : showError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load this widget"
            description={error!}
            action={
              <button
                onClick={load}
                className="rounded-lg border border-indigo-500/50 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
              >
                Retry
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState icon={AlertTriangle} title="No control failures recorded" description="No completed assessments have recorded a control failure yet." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="size"
              aspectRatio={4 / 3}
              stroke="none"
              isAnimationActive
              content={((tileProps: any) => <ControlFailureTreemapTile {...tileProps} onSelect={openControl} />) as unknown as ReactElement}
            >
              <Tooltip content={<ControlFailureTreemapTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        )}
      </div>
      <ControlFailureRateDrawer open={open} onOpenChange={setOpen} rows={rows} selectedId={sel} onSelect={setSel} />
    </Card>
  );
}
